import React, { useState, useEffect, useRef, useMemo } from "react";
import { MapPin, Globe, Plus, Edit, Trash2, Search, ChevronDown, Building2, Check, FileText, Loader2, Upload, AlertCircle, CheckCircle, XCircle } from "lucide-react";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/Button";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import AdminHeader from "../../Headers/AdminHeader";
import { useAppDispatch, useAppSelector } from "../../redux/store";
import {
  fetchCountries,
  fetchCountriesPage,
  createCountry,
  updateCountry,
  deleteCountry,
  fetchRegionsByCountry,
  createRegion,
  updateRegion,
  deleteRegion,
  fetchCitiesByRegion,
  fetchCitiesByCountry,
  createCity,
  updateCity,
  deleteCity,
  clearCountriesError,
  clearRegionsError,
  clearCitiesError,
  setCountriesSearchQuery,
  setRegionsSearchQuery,
  setCitiesSearchQuery,
  bulkImportCsv
} from "../../redux/Slices/LocationSlice";
import { LocationCountry, LocationRegion, LocationCity, CountryCreateDto, RegionCreateDto, CityCreateDto, CityTypeEnum } from "../../modules/shared/model";
import SEOSettingsModal from "./SEOSettingsModal";
import CountryDropdown from "../../components/ui/CountryDropdown";

const LocationManagement = () => {
  const dispatch = useAppDispatch();
  const {
    countries,
    countriesLoading,
    countriesError,
    countriesTotalCount,
    countriesCurrentPage,
    countriesHasNextPage,
    countriesHasPreviousPage,
    countriesSearchQuery,
    regions,
    regionsLoading,
    regionsError,
    regionsSearchQuery,
    cities,
    citiesLoading,
    citiesError,
    citiesSearchQuery
  } = useAppSelector(state => state.locations);

  const [activeTab, setActiveTab] = useState("countries");
  const [searchQuery, setSearchQuery] = useState("");
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<LocationCountry | LocationRegion | LocationCity | null>(null);
  const [selectedCountry, setSelectedCountry] = useState<LocationCountry | null>(null);
  const [selectedRegion, setSelectedRegion] = useState<LocationRegion | null>(null);
  const [seoDialogOpen, setSeoDialogOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [csvImportDialogOpen, setCsvImportDialogOpen] = useState(false);
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [importResult, setImportResult] = useState<any>(null);
  const [isImporting, setIsImporting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [formData, setFormData] = useState<any>({
    id: "", name: "", initial: "", type: { id: CityTypeEnum.City, name: "City" }
  });
  const [countrySearchInput, setCountrySearchInput] = useState(countriesSearchQuery);

  const locationCountryMap = useMemo(() => {
    const map = new Map<string, LocationCountry>();
    countries.forEach((country) => {
      map.set(country.id, country);
    });
    return map;
  }, [countries]);

  const convertToDropdownCountry = (country: LocationCountry | { id: string; name: string; initial?: string } | null | undefined) => {
    if (!country) {
      return null;
    }
    return {
      id: country.id,
      name: country.name,
      code: (country as any).initial || (country as any).code || ""
    };
  };

  const countryIdForForm = formData?.country?.id || "";
  const availableRegionsForForm = useMemo(() => {
    if (!countryIdForForm) {
      return regions;
    }
    return regions.filter((region) => region.country?.id === countryIdForForm);
  }, [countryIdForForm, regions]);

  // Fetch data on component mount and page changes
  useEffect(() => {
    dispatch(fetchCountriesPage({ page: currentPage, search: countriesSearchQuery }));
  }, [dispatch, currentPage, countriesSearchQuery]);

  // Clear errors when switching tabs
  useEffect(() => {
    dispatch(clearCountriesError());
    dispatch(clearRegionsError());
    dispatch(clearCitiesError());
  }, [activeTab, dispatch]);

  useEffect(() => {
    setCountrySearchInput(countriesSearchQuery);
  }, [countriesSearchQuery]);

  useEffect(() => {
    if (countrySearchInput === countriesSearchQuery) {
      return;
    }
    const handler = setTimeout(() => {
      dispatch(setCountriesSearchQuery(countrySearchInput));
      setCurrentPage(1);
    }, 400);
    return () => clearTimeout(handler);
  }, [countrySearchInput, countriesSearchQuery, dispatch]);

  const resetFormData = () => {
    setFormData({
      id: "", name: "", initial: "", type: { id: CityTypeEnum.City, name: "City" }
    });
  };

  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleAdd = () => {
    resetFormData();
    setAddDialogOpen(true);
  };

  const handleEdit = (item: LocationCountry | LocationRegion | LocationCity) => {
    setSelectedItem(item);
    if ("initial" in item) {
      // Country
      setFormData({ id: item.id, name: item.name, initial: item.initial });
    } else if ("country" in item && !("region" in item)) {
      // Region
      setFormData({ id: item.id, name: item.name, country: item.country });
    } else {
      // City
      setFormData({ 
        id: item.id, 
        name: item.name, 
        type: { id: Number(item.type.id), name: item.type.name }, 
        region: item.region, 
        country: item.country 
      });
    }
    setEditDialogOpen(true);
  };

  const handleDelete = (item: LocationCountry | LocationRegion | LocationCity) => {
    setSelectedItem(item);
    setDeleteDialogOpen(true);
  };

  const handleSeo = (item: LocationCountry | LocationRegion | LocationCity) => {
    setSelectedItem(item);
    setSeoDialogOpen(true);
  };

  const handleCsvImport = () => {
    setCsvImportDialogOpen(true);
    setImportResult(null);
    setCsvFile(null);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.name.endsWith(".csv")) {
        alert("Please select a CSV file");
        return;
      }
      setCsvFile(file);
      setImportResult(null);
    }
  };

  const handleCsvUpload = async () => {
    if (!csvFile) {
      alert("Please select a CSV file first");
      return;
    }

    setIsImporting(true);
    try {
      const result = await dispatch(bulkImportCsv(csvFile));
      
      if (result.payload) {
        setImportResult(result.payload);
        // Refresh the countries list after successful import
        await dispatch(fetchCountriesPage({ page: currentPage, search: countriesSearchQuery }));
      }
    } catch (error: any) {
      setImportResult({
        success: false,
        message: error?.message || "Failed to import CSV file",
        stats: { errors: [error?.message || "Unknown error"] }
      });
    } finally {
      setIsImporting(false);
    }
  };

  const downloadCsvTemplate = () => {
    const csvContent = "country_name,country_code,region_name,city_name,city_type\nUnited States,US,California,Los Angeles,City\nUnited States,US,California,San Francisco,Metropolitan\nCanada,CA,Ontario,Toronto,City\nUnited Kingdom,UK,London,London,City\nUnited Kingdom,UK,Scotland,Edinburgh,City";
    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "location_import_template.csv";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const handleAddSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (activeTab === "countries") {
      const countryData: CountryCreateDto = {
        name: formData.name,
        initial: formData.initial
      };
      await dispatch(createCountry(countryData));
    } else if (activeTab === "regions" && selectedCountry) {
      const regionData: RegionCreateDto = {
        name: formData.name,
        country: { id: selectedCountry.id, initial: selectedCountry.initial, name: selectedCountry.name }
      };
      await dispatch(createRegion(regionData));
      // Refresh countries data to update region counts
      await dispatch(fetchCountriesPage({ page: currentPage, search: countriesSearchQuery }));
    } else if (activeTab === "cities" && selectedRegion && selectedCountry) {
      const cityData: CityCreateDto = {
        name: formData.name,
        type: formData.type,
        region: { id: selectedRegion.id, name: selectedRegion.name, country: selectedRegion.country },
        country: { id: selectedCountry.id, initial: selectedCountry.initial, name: selectedCountry.name }
      };
      await dispatch(createCity(cityData));
      // Refresh countries data to update city counts
      await dispatch(fetchCountriesPage({ page: currentPage, search: countriesSearchQuery }));
      // Also refresh regions data if we're viewing regions
      if (selectedCountry) {
        await dispatch(fetchRegionsByCountry(selectedCountry.id));
      }
    }

    setAddDialogOpen(false);
    resetFormData();
  };

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (selectedItem && "initial" in selectedItem) {
      // Country
      await dispatch(updateCountry({ id: selectedItem.id, data: { name: formData.name, initial: formData.initial } }));
    } else if (selectedItem && "country" in selectedItem && !("region" in selectedItem)) {
      // Region
      await dispatch(updateRegion({ id: selectedItem.id, data: { name: formData.name, country: formData.country } }));
      // Refresh countries data to update region counts
      await dispatch(fetchCountriesPage({ page: currentPage, search: countriesSearchQuery }));
    } else if (selectedItem && "region" in selectedItem) {
      // City
      await dispatch(updateCity({ id: selectedItem.id, data: { name: formData.name, type: formData.type, region: formData.region, country: formData.country } }));
      // Refresh countries data to update city counts
      await dispatch(fetchCountriesPage({ page: currentPage, search: countriesSearchQuery }));
      // Also refresh regions data if we're viewing regions
      if (selectedCountry) {
        await dispatch(fetchRegionsByCountry(selectedCountry.id));
      }
    }

    setEditDialogOpen(false);
  };

  const handleDeleteConfirm = async () => {
    if (selectedItem && "initial" in selectedItem) {
      // Country
      await dispatch(deleteCountry(selectedItem.id));
      // Refresh countries data after deletion
      await dispatch(fetchCountriesPage({ page: currentPage, search: countriesSearchQuery }));
    } else if (selectedItem && "country" in selectedItem && !("region" in selectedItem)) {
      // Region
      await dispatch(deleteRegion(selectedItem.id));
      // Refresh countries data to update region counts
      await dispatch(fetchCountriesPage({ page: currentPage, search: countriesSearchQuery }));
    } else if (selectedItem && "region" in selectedItem) {
      // City
      await dispatch(deleteCity(selectedItem.id));
      // Refresh countries data to update city counts
      await dispatch(fetchCountriesPage({ page: currentPage, search: countriesSearchQuery }));
      // Also refresh regions data if we're viewing regions
      if (selectedCountry) {
        await dispatch(fetchRegionsByCountry(selectedCountry.id));
      }
    }

    setDeleteDialogOpen(false);
  };

  // Handle country selection to fetch regions
  const handleCountrySelect = async (country: LocationCountry) => {
    setSelectedCountry(country);
    setSelectedRegion(null);

    // If we're on countries tab, switch to regions tab and fetch regions
    if (activeTab === "countries") {
      setActiveTab("regions");
      await dispatch(fetchRegionsByCountry(country.id));
    } else if (activeTab === "regions" || activeTab === "cities") {
      await dispatch(fetchRegionsByCountry(country.id));
    }
  };

  // Handle region selection to fetch cities
  const handleRegionSelect = async (region: LocationRegion) => {
    setSelectedRegion(region);

    // If we're on regions tab, switch to cities tab and fetch cities
    if (activeTab === "regions") {
      setActiveTab("cities");
      await dispatch(fetchCitiesByRegion(region.id));
    } else if (activeTab === "cities") {
      await dispatch(fetchCitiesByRegion(region.id));
    }
  };

  // Handle tab change
  const handleTabChange = async (tab: string) => {
    setActiveTab(tab);
    setSelectedCountry(null);
    setSelectedRegion(null);

    if (tab === "regions" && selectedCountry) {
      await dispatch(fetchRegionsByCountry(selectedCountry.id));
    } else if (tab === "cities" && selectedRegion) {
      await dispatch(fetchCitiesByRegion(selectedRegion.id));
    } else if (tab === "cities" && selectedCountry) {
      await dispatch(fetchCitiesByCountry(selectedCountry.id));
    }
  };

  // Handle search for countries
  const handleCountrySearch = (query: string) => {
    setCountrySearchInput(query);
  };

  // Handle search for regions (from selected country)
  const handleRegionSearch = (query: string) => {
    dispatch(setRegionsSearchQuery(query));
    // Filter regions from selected country
    if (selectedCountry) {
      const filteredRegions = selectedCountry.regions?.filter(region =>
        region.name.toLowerCase().includes(query.toLowerCase())
      ) || [];
      // Update regions in state (this would need a new action)
    }
  };

  // Handle search for cities (from selected region or country)
  const handleCitySearch = (query: string) => {
    dispatch(setCitiesSearchQuery(query));
    // Filter cities from selected region or country
    if (selectedRegion) {
      const filteredCities = selectedRegion.cities?.filter(city =>
        city.name.toLowerCase().includes(query.toLowerCase())
      ) || [];
      // Update cities in state (this would need a new action)
    } else if (selectedCountry) {
      const filteredCities = selectedCountry.cities?.filter(city =>
        city.name.toLowerCase().includes(query.toLowerCase())
      ) || [];
      // Update cities in state (this would need a new action)
    }
  };

  // Handle pagination
  const handleNextPage = () => {
    if (countriesHasNextPage && !countriesLoading) {
      setCurrentPage(prev => prev + 1);
    }
  };

  const handlePreviousPage = () => {
    if (currentPage > 1 && !countriesLoading) {
      setCurrentPage(prev => prev - 1);
    }
  };

  const getFilteredItems = () => {
    if (activeTab === "countries") {
      return countries; // Already filtered by backend
    } else if (activeTab === "regions") {
      const query = regionsSearchQuery.toLowerCase();
      return regions.filter(region => region.name.toLowerCase().includes(query));
    } else if (activeTab === "cities") {
      const query = citiesSearchQuery.toLowerCase();
      return cities.filter(city => city.name.toLowerCase().includes(query));
    }
    return [];
  };

  const isLoading = countriesLoading || regionsLoading || citiesLoading;
  const totalPages = Math.ceil((countriesTotalCount || 0) / 10);

  return (
    <div className="min-h-screen bg-gray-50" style={{ textAlign: "left" }}>
      <AdminHeader />
      <main className="lg:pl-64 pt-16">
        <div className="p-3 sm:p-4 lg:p-6">
          <div className="max-w-7xl mx-auto space-y-4 sm:space-y-6">
            <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
              <div className="flex-1">
                <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900">Location Management</h1>
                <p className="text-sm sm:text-base text-gray-600 mt-1">Manage countries, regions, and cities for your platform</p>
              </div>
              <div className="flex gap-2 flex-col sm:flex-row w-full lg:w-auto">
                <Button variant="outline" className="w-full sm:w-auto text-sm" onClick={handleCsvImport}>
                  <Upload className="h-4 w-4 mr-2" />Bulk Import CSV
                </Button>
                <Button onClick={handleAdd} className="w-full sm:w-auto text-sm">
                  <Plus className="h-4 w-4 mr-2" />
                  {activeTab === "countries" ? "Add Country" : activeTab === "regions" ? "Add Region" : "Add City"}
                </Button>
              </div>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
              <Card className="p-3 sm:p-4 lg:p-6">
                <div className="flex justify-between items-start">
                  <div className="min-w-0 flex-1">
                    <p className="text-xs sm:text-sm text-gray-500 truncate">Total Countries</p>
                    <p className="text-lg sm:text-xl lg:text-2xl font-semibold mt-1">{countriesTotalCount || 0}</p>
                  </div>
                  <div className="p-2 bg-blue-100 rounded-lg flex-shrink-0 ml-2">
                    <Globe className="h-4 w-4 sm:h-5 sm:w-5 text-blue-600" />
                  </div>
                </div>
              </Card>
              <Card className="p-3 sm:p-4 lg:p-6">
                <div className="flex justify-between items-start">
                  <div className="min-w-0 flex-1">
                    <p className="text-xs sm:text-sm text-gray-500 truncate">Total Regions</p>
                    <p className="text-lg sm:text-xl lg:text-2xl font-semibold mt-1">{regions.length}</p>
                  </div>
                  <div className="p-2 bg-green-100 rounded-lg flex-shrink-0 ml-2">
                    <MapPin className="h-4 w-4 sm:h-5 sm:w-5 text-green-600" />
                  </div>
                </div>
              </Card>
              <Card className="p-3 sm:p-4 lg:p-6">
                <div className="flex justify-between items-start">
                  <div className="min-w-0 flex-1">
                    <p className="text-xs sm:text-sm text-gray-500 truncate">Total Cities</p>
                    <p className="text-lg sm:text-xl lg:text-2xl font-semibold mt-1">
                      {cities.length + countries.reduce((total, country) =>
                        total + (country.regions?.reduce((regionTotal, region) =>
                          regionTotal + (region.cities?.length || 0), 0) || 0), 0)}
                    </p>
                  </div>
                  <div className="p-2 bg-purple-100 rounded-lg flex-shrink-0 ml-2">
                    <Building2 className="h-4 w-4 sm:h-5 sm:w-5 text-purple-600" />
                  </div>
                </div>
              </Card>
              <Card className="p-3 sm:p-4 lg:p-6">
                <div className="flex justify-between items-start">
                  <div className="min-w-0 flex-1">
                    <p className="text-xs sm:text-sm text-gray-500 truncate">Total Locations</p>
                    <p className="text-lg sm:text-xl lg:text-2xl font-semibold mt-1">
                      {countries.length + regions.length + cities.length + countries.reduce((total, country) =>
                        total + (country.regions?.reduce((regionTotal, region) =>
                          regionTotal + (region.cities?.length || 0), 0) || 0), 0)}
                    </p>
                  </div>
                  <div className="p-2 bg-yellow-100 rounded-lg flex-shrink-0 ml-2">
                    <Check className="h-4 w-4 sm:h-5 sm:w-5 text-yellow-600" />
                  </div>
                </div>
              </Card>
            </div>

            <div className="mb-6 sm:mb-8 border-b overflow-x-auto">
              <div className="flex gap-2 sm:gap-4 px-1 sm:px-2 min-w-max">
                {["countries", "regions", "cities"].map((tab) => (
                  <button
                    key={tab}
                    onClick={() => handleTabChange(tab)}
                    className={`py-2 sm:py-3 px-2 sm:px-3 text-xs sm:text-sm relative whitespace-nowrap ${activeTab === tab ? "text-blue-600" : "text-gray-600 hover:text-gray-900"}`}
                  >
                    <span>{tab.charAt(0).toUpperCase() + tab.slice(1)}</span>
                    {activeTab === tab && (
                      <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600" />
                    )}
                  </button>
                ))}
              </div>
              <div className="flex flex-col sm:flex-row gap-2 sm:gap-4 mt-3 sm:mt-4">
                <div className="relative w-full sm:w-64">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    type="text"
                    placeholder={`Search ${activeTab}...`}
                    value={activeTab === "countries" ? countrySearchInput : activeTab === "regions" ? regionsSearchQuery : citiesSearchQuery}
                    onChange={(e) => {
                      const query = e.target.value;
                      if (activeTab === "countries") {
                        handleCountrySearch(query);
                      } else if (activeTab === "regions") {
                        handleRegionSearch(query);
                      } else if (activeTab === "cities") {
                        handleCitySearch(query);
                      }
                    }}
                    className="w-full pl-9 text-sm"
                  />
                </div>
              </div>
            </div>

            {(activeTab === "regions" || activeTab === "cities") && (
              <div className="flex items-center gap-1 sm:gap-2 mb-4 flex-wrap overflow-x-auto">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => { setSelectedCountry(null); setSelectedRegion(null); }}
                  className="text-xs sm:text-sm whitespace-nowrap"
                >
                  <Globe className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />All Countries
                </Button>
                {selectedCountry && (
                  <>
                    <ChevronDown className="h-3 w-3 sm:h-4 sm:w-4 text-gray-400 flex-shrink-0" />
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setSelectedRegion(null)}
                      className="text-xs sm:text-sm whitespace-nowrap"
                    >
                      <MapPin className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />{selectedCountry.name}
                    </Button>
                  </>
                )}
                {selectedRegion && activeTab === "cities" && (
                  <>
                    <ChevronDown className="h-3 w-3 sm:h-4 sm:w-4 text-gray-400 flex-shrink-0" />
                    <Button variant="ghost" size="sm" className="text-xs sm:text-sm whitespace-nowrap">
                      <Building2 className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />{selectedRegion.name}
                    </Button>
                  </>
                )}
              </div>
            )}

            {isLoading ? (
              <div className="flex justify-center items-center py-8">
                <Loader2 className="h-6 w-6 sm:h-8 sm:w-8 animate-spin text-blue-600" />
                <span className="ml-2 text-sm sm:text-base text-gray-600">Loading...</span>
              </div>
            ) : getFilteredItems().length === 0 ? (
              <div className="col-span-full flex flex-col items-center justify-center py-12 text-center">
                <Globe className="h-12 w-12 text-gray-400 mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No {activeTab} found</h3>
                <p className="text-gray-500 mb-4">
                  {countriesSearchQuery ? `No countries match "${countriesSearchQuery}"` : "No countries available"}
                </p>
                {countriesSearchQuery && (
                  <Button 
                    variant="outline" 
                    onClick={() => {
                      dispatch(setCountriesSearchQuery(""));
                      setCurrentPage(1);
                    }}
                    className="text-sm"
                  >
                    Clear search
                  </Button>
                )}
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4">
                {activeTab === "countries" && getFilteredItems().map((country) => (
                  <Card key={country.id} className="overflow-hidden hover:shadow-md transition-shadow">
                    <div className="p-3 sm:p-4 lg:p-6">
                      <div className="flex justify-between items-start gap-2 sm:gap-4">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-1 sm:gap-2 flex-wrap">
                            <h3 className="font-medium text-sm sm:text-base truncate">{country.name}</h3>
                            <Badge variant="outline" className="text-xs flex-shrink-0">{country.initial}</Badge>
                          </div>
                          <p className="text-xs sm:text-sm text-gray-500 mt-1">Created: {new Date(country.createdAt).toLocaleDateString()}</p>
                        </div>
                        <div className="flex gap-1 flex-shrink-0">
                          <Button variant="ghost" size="sm" onClick={() => handleSeo(country)} className="text-xs p-1 sm:p-2">
                            <FileText className="h-3 w-3 sm:h-4 sm:w-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleEdit(country)} className="text-xs p-1 sm:p-2">
                            <Edit className="h-3 w-3 sm:h-4 sm:w-4" />
                          </Button>
                          <Button variant="ghost" size="sm" className="text-red-600 text-xs p-1 sm:p-2" onClick={() => handleDelete(country)}>
                            <Trash2 className="h-3 w-3 sm:h-4 sm:w-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                    <div className="bg-gray-50 p-2 sm:p-3 lg:p-4 flex justify-between items-center border-t flex-col sm:flex-row gap-2">
                      <div className="flex gap-2 text-xs sm:text-sm flex-wrap">
                        <span className="text-gray-500">{country.regions?.length || 0} Regions</span>
                        <span className="text-gray-500">
                          {(country.regions?.reduce((total, region) => total + (region.cities?.length || 0), 0) || 0)} Cities
                        </span>
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => handleCountrySelect(country)} className="text-xs w-full sm:w-auto">
                        View Regions
                      </Button>
                    </div>
                  </Card>
                ))}

                {/* Pagination for Countries */}
                {activeTab === "countries" && (
                  <div className="col-span-full flex flex-col sm:flex-row justify-between items-center gap-3 sm:gap-4 mt-4 sm:mt-6">
                    <div className="text-xs sm:text-sm text-gray-500 text-center sm:text-left">
                      Showing {(currentPage - 1) * 10 + 1}-{Math.min(currentPage * 10, countriesTotalCount || 0)} of {countriesTotalCount || 0} countries
                      {countriesSearchQuery && (
                        <span className="ml-2 text-blue-600">
                          (filtered by &quot;{countriesSearchQuery}&quot;)
                        </span>
                      )}
                      <span className="ml-2 text-gray-400">
                        (Page {currentPage} of {totalPages})
                      </span>
                    </div>
                    <div className="flex gap-2 items-center">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handlePreviousPage}
                        disabled={currentPage === 1 || countriesLoading}
                        className="text-xs"
                      >
                        {countriesLoading ? (
                          <Loader2 className="h-3 w-3 animate-spin mr-1" />
                        ) : null}
                        Previous
                      </Button>
                      <div className="flex items-center gap-1">
                        <span className="text-xs text-gray-500">Page</span>
                        <input
                          type="number"
                          min="1"
                          max={totalPages}
                          value={currentPage}
                          onChange={(e) => {
                            const page = parseInt(e.target.value);
                            if (page >= 1 && page <= totalPages) {
                              setCurrentPage(page);
                            }
                          }}
                          className="w-12 h-6 text-xs text-center border border-gray-300 rounded px-1"
                          disabled={countriesLoading}
                        />
                        <span className="text-xs text-gray-500">of {totalPages}</span>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleNextPage}
                        disabled={currentPage === totalPages || !countriesHasNextPage || countriesLoading}
                        className="text-xs"
                      >
                        {countriesLoading ? (
                          <Loader2 className="h-3 w-3 animate-spin mr-1" />
                        ) : null}
                        Next
                      </Button>
                    </div>
                  </div>
                )}

                {activeTab === "regions" && (!selectedCountry ? (
                  countries.map((country) => (
                    <Card key={country.id} className="p-3 sm:p-4 lg:p-6 hover:shadow-md transition-shadow cursor-pointer" onClick={() => handleCountrySelect(country)}>
                      <div className="flex justify-between items-center gap-2">
                        <div className="min-w-0 flex-1">
                          <h3 className="font-medium text-sm sm:text-base truncate">{country.name}</h3>
                          <p className="text-xs sm:text-sm text-gray-500 mt-1">
                            {country.regions?.length || 0} Regions
                          </p>
                        </div>
                        <ChevronDown className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400 flex-shrink-0" />
                      </div>
                    </Card>
                  ))
                ) : (
                  getFilteredItems().map((region) => (
                    <Card key={region.id} className="overflow-hidden hover:shadow-md transition-shadow">
                      <div className="p-3 sm:p-4 lg:p-6">
                        <div className="flex justify-between items-start gap-2 sm:gap-4">
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-1 sm:gap-2 flex-wrap">
                              <h3 className="font-medium text-sm sm:text-base truncate">{region.name}</h3>
                            </div>
                            <p className="text-xs sm:text-sm text-gray-500 mt-1">Country: {region.country.name}</p>
                            <p className="text-xs sm:text-sm text-gray-500">Created: {new Date(region.createdAt).toLocaleDateString()}</p>
                          </div>
                          <div className="flex gap-1 flex-shrink-0">
                            <Button variant="ghost" size="sm" onClick={() => handleSeo(region)} className="text-xs p-1 sm:p-2">
                              <FileText className="h-3 w-3 sm:h-4 sm:w-4" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => handleEdit(region)} className="text-xs p-1 sm:p-2">
                              <Edit className="h-3 w-3 sm:h-4 sm:w-4" />
                            </Button>
                            <Button variant="ghost" size="sm" className="text-red-600 text-xs p-1 sm:p-2" onClick={() => handleDelete(region)}>
                              <Trash2 className="h-3 w-3 sm:h-4 sm:w-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                      <div className="bg-gray-50 p-2 sm:p-3 lg:p-4 flex justify-between items-center border-t flex-col sm:flex-row gap-2">
                        <div className="flex gap-2 text-xs sm:text-sm flex-wrap">
                          <span className="text-gray-500">{region.cities?.length || 0} Cities</span>
                        </div>
                        <Button variant="ghost" size="sm" onClick={() => handleRegionSelect(region)} className="text-xs w-full sm:w-auto">
                          View Cities
                        </Button>
                      </div>
                    </Card>
                  ))
                ))}

                {activeTab === "cities" && (!selectedCountry ? (
                  countries.map((country) => (
                    <Card key={country.id} className="p-3 sm:p-4 lg:p-6 hover:shadow-md transition-shadow cursor-pointer" onClick={() => handleCountrySelect(country)}>
                      <div className="flex justify-between items-center gap-2">
                        <div className="min-w-0 flex-1">
                          <h3 className="font-medium text-sm sm:text-base truncate">{country.name}</h3>
                          <p className="text-xs sm:text-sm text-gray-500 mt-1">
                            {(country.regions?.reduce((total, region) => total + (region.cities?.length || 0), 0) || 0)} Cities
                          </p>
                        </div>
                        <ChevronDown className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400 flex-shrink-0" />
                      </div>
                    </Card>
                  ))
                ) : !selectedRegion ? (
                  regions.map((region) => (
                    <Card key={region.id} className="p-3 sm:p-4 lg:p-6 hover:shadow-md transition-shadow cursor-pointer" onClick={() => handleRegionSelect(region)}>
                      <div className="flex justify-between items-center gap-2">
                        <div className="min-w-0 flex-1">
                          <h3 className="font-medium text-sm sm:text-base truncate">{region.name}</h3>
                          <p className="text-xs sm:text-sm text-gray-500 mt-1">{region.cities?.length || 0} Cities</p>
                        </div>
                        <ChevronDown className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400 flex-shrink-0" />
                      </div>
                    </Card>
                  ))
                ) : (
                  getFilteredItems().map((city) => (
                    <Card key={city.id} className="overflow-hidden hover:shadow-md transition-shadow">
                      <div className="p-3 sm:p-4 lg:p-6">
                        <div className="flex justify-between items-start gap-2 sm:gap-4">
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-1 sm:gap-2 flex-wrap">
                              <h3 className="font-medium text-sm sm:text-base truncate">{city.name}</h3>
                              <Badge variant="outline" className="text-xs flex-shrink-0">
                                {city.type.id === CityTypeEnum.City ? "City" : "Metropolitan"}
                              </Badge>
                            </div>
                            <p className="text-xs sm:text-sm text-gray-500 mt-1">Region: {city.region.name}</p>
                            <p className="text-xs sm:text-sm text-gray-500">Country: {city.country.name}</p>
                            <p className="text-xs sm:text-sm text-gray-500">Created: {new Date(city.createdAt).toLocaleDateString()}</p>
                          </div>
                          <div className="flex gap-1 flex-shrink-0">
                            <Button variant="ghost" size="sm" onClick={() => handleSeo(city)} className="text-xs p-1 sm:p-2">
                              <FileText className="h-3 w-3 sm:h-4 sm:w-4" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => handleEdit(city)} className="text-xs p-1 sm:p-2">
                              <Edit className="h-3 w-3 sm:h-4 sm:w-4" />
                            </Button>
                            <Button variant="ghost" size="sm" className="text-red-600 text-xs p-1 sm:p-2" onClick={() => handleDelete(city)}>
                              <Trash2 className="h-3 w-3 sm:h-4 sm:w-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    </Card>
                  ))
                ))}
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Add Location Dialog */}
      <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
        <DialogContent className="w-[95vw] sm:w-full sm:max-w-[600px] max-h-[90vh] sm:max-h-[80vh] p-4 sm:p-6">
          <DialogHeader>
            <DialogTitle className="text-lg sm:text-xl">Add New Location</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddSubmit} className="space-y-4 sm:space-y-6">
            <div className="max-h-[50vh] sm:max-h-[60vh] overflow-y-auto pr-1 sm:pr-2">
              <div className="grid grid-cols-1 gap-4 sm:gap-6">
                {activeTab === "countries" && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                      <Input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleFormChange}
                        placeholder="Enter country name"
                        required
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Country Code</label>
                      <Input
                        type="text"
                        name="initial"
                        value={formData.initial}
                        onChange={handleFormChange}
                        placeholder="e.g., US, CA"
                        maxLength={3}
                        required
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                      />
                    </div>
                  </>
                )}

                {activeTab === "regions" && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Country</label>
                      <CountryDropdown
                        className="mt-1"
                        selectedCountry={convertToDropdownCountry(formData.country || selectedCountry)}
                        onCountrySelect={(countryOption) => {
                          if (!countryOption) {
                            setFormData((prev: any) => ({ ...prev, country: null }));
                            setSelectedCountry(null);
                            return;
                          }
                          const locationCountry = locationCountryMap.get(countryOption.id) || {
                            id: countryOption.id,
                            name: countryOption.name,
                            initial: countryOption.code || ""
                          } as LocationCountry;

                          setFormData((prev: any) => ({
                            ...prev,
                            country: {
                              id: locationCountry.id,
                              initial: locationCountry.initial,
                              name: locationCountry.name
                            }
                          }));
                          setSelectedCountry(locationCountry);
                        }}
                        placeholder="Select country"
                        autoDetect={false}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Region Name</label>
                      <Input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleFormChange}
                        placeholder="Enter region name"
                        required
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                      />
                    </div>
                  </>
                )}

                {activeTab === "cities" && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Country</label>
                      <CountryDropdown
                        className="mt-1"
                        selectedCountry={convertToDropdownCountry(formData.country || selectedCountry)}
                        onCountrySelect={(countryOption) => {
                          if (!countryOption) {
                            setFormData((prev: any) => ({ ...prev, country: null, region: null }));
                            setSelectedCountry(null);
                            setSelectedRegion(null);
                            return;
                          }

                          const locationCountry = locationCountryMap.get(countryOption.id) || {
                            id: countryOption.id,
                            name: countryOption.name,
                            initial: countryOption.code || ""
                          } as LocationCountry;

                          setFormData((prev: any) => ({
                            ...prev,
                            country: {
                              id: locationCountry.id,
                              initial: locationCountry.initial,
                              name: locationCountry.name
                            },
                            region: null
                          }));
                          setSelectedCountry(locationCountry);
                          setSelectedRegion(null);
                          dispatch(fetchRegionsByCountry(locationCountry.id));
                        }}
                        placeholder="Select country"
                        autoDetect={false}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Region</label>
                      <select
                        name="region"
                        value={formData.region?.id || ""}
                        onChange={(e) => {
                          const region = regions.find(r => r.id === e.target.value);
                          setFormData({ ...formData, region: region ? { id: region.id, name: region.name, country: region.country } : null });
                        }}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all disabled:bg-gray-100 disabled:text-gray-400"
                        disabled={!formData.country}
                      >
                        <option value="">Select Region</option>
                        {availableRegionsForForm.map(region => (
                          <option key={region.id} value={region.id}>{region.name}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">City Type</label>
                      <select
                        name="type"
                        value={formData.type?.id || CityTypeEnum.City}
                        onChange={(e) => {
                          const typeId = parseInt(e.target.value) as CityTypeEnum;
                          const typeName = typeId === CityTypeEnum.City ? "City" : "Metropolitan";
                          setFormData({ ...formData, type: { id: typeId, name: typeName } });
                        }}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                      >
                        <option value={CityTypeEnum.City}>City</option>
                        <option value={CityTypeEnum.Metropolitan}>Metropolitan</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">City Name</label>
                      <Input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleFormChange}
                        placeholder="Enter city name"
                        required
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                      />
                    </div>
                  </>
                )}
              </div>
            </div>
            <DialogFooter className="flex flex-col sm:flex-row gap-2 sm:gap-0">
              <Button type="button" variant="outline" onClick={() => setAddDialogOpen(false)} className="w-full sm:w-auto text-sm">Cancel</Button>
              <Button type="submit" className="w-full sm:w-auto text-sm">Add Location</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Location Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="w-[95vw] sm:w-full sm:max-w-[600px] max-h-[90vh] sm:max-h-[80vh] p-4 sm:p-6">
          <DialogHeader>
            <DialogTitle className="text-lg sm:text-xl">Edit Location</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleEditSubmit} className="space-y-4 sm:space-y-6">
            <div className="max-h-[50vh] sm:max-h-[60vh] overflow-y-auto pr-1 sm:pr-2">
              <div className="grid grid-cols-1 gap-4 sm:gap-6">
                {selectedItem && "initial" in selectedItem && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                      <Input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleFormChange}
                        placeholder="Enter country name"
                        required
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Country Code</label>
                      <Input
                        type="text"
                        name="initial"
                        value={formData.initial}
                        onChange={handleFormChange}
                        placeholder="e.g., US, CA"
                        maxLength={3}
                        required
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                      />
                    </div>
                  </>
                )}

                {selectedItem && "country" in selectedItem && !("region" in selectedItem) && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                      <Input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleFormChange}
                        placeholder="Enter region name"
                        required
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Country</label>
                      <select
                        name="country"
                        value={formData.country?.id || ""}
                        onChange={(e) => {
                          const country = countries.find(c => c.id === e.target.value);
                          setFormData({ ...formData, country: country ? { id: country.id, initial: country.initial, name: country.name } : null });
                        }}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                      >
                        <option value="">Select Country</option>
                        {countries.map(country => (
                          <option key={country.id} value={country.id}>{country.name}</option>
                        ))}
                      </select>
                    </div>
                  </>
                )}

                {selectedItem && "region" in selectedItem && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                      <Input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleFormChange}
                        placeholder="Enter city name"
                        required
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">City Type</label>
                      <select
                        name="type"
                        value={formData.type?.id || CityTypeEnum.City}
                        onChange={(e) => {
                          const typeId = parseInt(e.target.value) as CityTypeEnum;
                          const typeName = typeId === CityTypeEnum.City ? "City" : "Metropolitan";
                          setFormData({ ...formData, type: { id: typeId, name: typeName } });
                        }}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                      >
                        <option value={CityTypeEnum.City}>City</option>
                        <option value={CityTypeEnum.Metropolitan}>Metropolitan</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Region</label>
                      <select
                        name="region"
                        value={formData.region?.id || ""}
                        onChange={(e) => {
                          const region = regions.find(r => r.id === e.target.value);
                          setFormData({ ...formData, region: region ? { id: region.id, name: region.name, country: region.country } : null });
                        }}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                      >
                        <option value="">Select Region</option>
                        {regions.map(region => (
                          <option key={region.id} value={region.id}>{region.name}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Country</label>
                      <select
                        name="country"
                        value={formData.country?.id || ""}
                        onChange={(e) => {
                          const country = countries.find(c => c.id === e.target.value);
                          setFormData({ ...formData, country: country ? { id: country.id, initial: country.initial, name: country.name } : null });
                        }}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                      >
                        <option value="">Select Country</option>
                        {countries.map(country => (
                          <option key={country.id} value={country.id}>{country.name}</option>
                        ))}
                      </select>
                    </div>
                  </>
                )}
              </div>
            </div>
            <DialogFooter className="flex flex-col sm:flex-row gap-2 sm:gap-0">
              <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)} className="w-full sm:w-auto text-sm">Cancel</Button>
              <Button type="submit" className="w-full sm:w-auto text-sm">Save Changes</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="w-[95vw] sm:w-full sm:max-w-[500px] p-4 sm:p-6">
          <DialogHeader>
            <DialogTitle className="text-lg sm:text-xl">Delete Location</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-gray-700">
              Are you sure you want to delete <strong>{selectedItem?.name}</strong>? This action cannot be undone.
            </p>
            {selectedItem && "initial" in selectedItem && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-700 text-sm">
                  <strong>Warning:</strong> Deleting this country will also delete all associated regions and cities.
                </p>
              </div>
            )}
            {selectedItem && "country" in selectedItem && !("region" in selectedItem) && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-700 text-sm">
                  <strong>Warning:</strong> Deleting this region will also delete all associated cities.
                </p>
              </div>
            )}
          </div>
          <DialogFooter className="flex flex-col sm:flex-row gap-2 sm:gap-0">
            <Button type="button" variant="outline" onClick={() => setDeleteDialogOpen(false)} className="w-full sm:w-auto text-sm">Cancel</Button>
            <Button type="button" variant="destructive" onClick={handleDeleteConfirm} className="w-full sm:w-auto text-sm">Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* SEO Settings Modal */}
      <SEOSettingsModal
        isOpen={seoDialogOpen}
        onClose={() => setSeoDialogOpen(false)}
        selectedItem={selectedItem}
      />

      {/* CSV Import Dialog */}
      <Dialog open={csvImportDialogOpen} onOpenChange={setCsvImportDialogOpen}>
        <DialogContent className="w-[95vw] sm:w-full sm:max-w-[700px] max-h-[90vh] p-4 sm:p-6">
          <DialogHeader>
            <DialogTitle className="text-lg sm:text-xl">Bulk Import from CSV</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 sm:space-y-6">
            {/* Instructions */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="font-medium text-blue-900 mb-2 flex items-center">
                <AlertCircle className="h-4 w-4 mr-2" />
                CSV Format Instructions
              </h4>
              <p className="text-sm text-blue-800 mb-2">
                Your CSV file must have the following columns:
              </p>
              <ul className="text-sm text-blue-800 list-disc list-inside space-y-1">
                <li><strong>countryName</strong> or <strong>country_name</strong> (required): Name of the country</li>
                <li><strong>countryCode</strong> or <strong>country_code</strong> (required): 2-letter code (e.g., US, CA, UK)</li>
                <li><strong>regionName</strong> or <strong>region_name</strong> (optional): Name of the region/state</li>
                <li><strong>cityName</strong> or <strong>city_name</strong> (optional): Name of the city</li>
                <li><strong>cityType</strong> or <strong>city_type</strong> (optional): Either &quot;City&quot; or &quot;Metropolitan&quot;</li>
              </ul>
              <p className="text-xs text-blue-700 mt-2 italic">
                Note: Headers can be in camelCase or snake_case format.
              </p>
            </div>

            {/* Download Template */}
            <div className="flex justify-between items-center">
              <p className="text-sm text-gray-600">Don&apos;t have a template?</p>
              <Button variant="outline" size="sm" onClick={downloadCsvTemplate}>
                <FileText className="h-4 w-4 mr-2" />
                Download Template
              </Button>
            </div>

            {/* File Upload */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">Upload CSV File</label>
              <div className="flex gap-2">
                <Input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv"
                  onChange={handleFileSelect}
                  className="flex-1"
                />
                {csvFile && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setCsvFile(null);
                      setImportResult(null);
                      if (fileInputRef.current) fileInputRef.current.value = "";
                    }}
                  >
                    <XCircle className="h-4 w-4" />
                  </Button>
                )}
              </div>
              {csvFile && (
                <p className="text-sm text-green-600 flex items-center">
                  <CheckCircle className="h-4 w-4 mr-1" />
                  Selected: {csvFile.name}
                </p>
              )}
            </div>

            {/* Import Results */}
            {importResult && (
              <div className={`border rounded-lg p-4 ${importResult.success ? "bg-green-50 border-green-200" : "bg-red-50 border-red-200"}`}>
                <h4 className={`font-medium mb-2 flex items-center ${importResult.success ? "text-green-900" : "text-red-900"}`}>
                  {importResult.success ? (
                    <CheckCircle className="h-5 w-5 mr-2" />
                  ) : (
                    <XCircle className="h-5 w-5 mr-2" />
                  )}
                  {importResult.message}
                </h4>
                
                {importResult.stats && (
                  <div className="space-y-2 text-sm">
                    <div className="grid grid-cols-2 gap-2">
                      <div className="bg-white bg-opacity-50 p-2 rounded">
                        <p className="font-medium">Total Rows: {importResult.stats.totalRows}</p>
                      </div>
                      <div className="bg-white bg-opacity-50 p-2 rounded">
                        <p className="text-green-700">Countries Created: {importResult.stats.countriesCreated}</p>
                        <p className="text-blue-700">Countries Found: {importResult.stats.countriesFound}</p>
                      </div>
                      <div className="bg-white bg-opacity-50 p-2 rounded">
                        <p className="text-green-700">Regions Created: {importResult.stats.regionsCreated}</p>
                        <p className="text-blue-700">Regions Found: {importResult.stats.regionsFound}</p>
                      </div>
                      <div className="bg-white bg-opacity-50 p-2 rounded">
                        <p className="text-green-700">Cities Created: {importResult.stats.citiesCreated}</p>
                        <p className="text-blue-700">Cities Found: {importResult.stats.citiesFound}</p>
                      </div>
                    </div>

                    {importResult.stats.errors && importResult.stats.errors.length > 0 && (
                      <div className="mt-3">
                        <p className="font-medium text-red-900 mb-1">Errors:</p>
                        <div className="max-h-40 overflow-y-auto bg-white bg-opacity-50 p-2 rounded">
                          {importResult.stats.errors.map((error: string, idx: number) => (
                            <p key={idx} className="text-xs text-red-800 mb-1">• {error}</p>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          <DialogFooter className="flex flex-col sm:flex-row gap-2 sm:gap-0">
            <Button 
              type="button" 
              variant="outline" 
              onClick={() => {
                setCsvImportDialogOpen(false);
                setCsvFile(null);
                setImportResult(null);
                if (fileInputRef.current) fileInputRef.current.value = "";
              }} 
              className="w-full sm:w-auto text-sm"
              disabled={isImporting}
            >
              {importResult ? "Close" : "Cancel"}
            </Button>
            {!importResult && (
              <Button 
                type="button" 
                onClick={handleCsvUpload} 
                className="w-full sm:w-auto text-sm"
                disabled={!csvFile || isImporting}
              >
                {isImporting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Importing...
                  </>
                ) : (
                  <>
                    <Upload className="h-4 w-4 mr-2" />
                    Import CSV
                  </>
                )}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LocationManagement;